import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import {
  fetchCharacterInventory,
  equipItem,
  unequipItem,
  swapEquipment,
  moveItem,
  equipItemLocal,
  unequipItemLocal,
  swapEquipmentLocal,
  moveItemLocal,
  clearError,
  selectCharacter,
  selectEquipment,
  selectInventory,
  selectCombat,
  selectLoading,
  selectError,
} from '../inventorySlice'
import { canEquipItemInSlot as canEquipItemInSlotUtil } from '../utils/slotUtils'

export const useInventory = () => {
  const dispatch = useDispatch()
  const { slug: characterName } = useParams()

  // Selectores
  const character = useSelector(selectCharacter)
  const equipment = useSelector(selectEquipment)
  const inventory = useSelector(selectInventory)
  const combat = useSelector(selectCombat)
  const loading = useSelector(selectLoading)
  const error = useSelector(selectError)

  // Cargar inventario al montar el componente
  useEffect(() => {
    if (characterName) {
      // Solo usar la API real, sin fallback
      dispatch(fetchCharacterInventory(characterName))
        .unwrap()
        .catch((error) => {
          console.error('Error cargando inventario desde API:', error)
        })
    }
  }, [dispatch, characterName])

  // Funciones para interactuar con el inventario
  const handleEquipItem = async (item, slotType, inventoryIndex) => {
    try {
      // Actualización optimista
      dispatch(equipItemLocal({ item, slotType, inventoryIndex }))

      // Llamada a la API
      await dispatch(
        equipItem({
          characterName,
          itemId: item.id,
          slotType,
        })
      ).unwrap()
    } catch (error) {
      console.error('Error equipando item:', error)
      refreshInventory()
    }
  }

  const handleUnequipItem = async (slotType) => {
    try {
      // Actualización optimista
      dispatch(unequipItemLocal({ slotType }))

      // Llamada a la API
      await dispatch(
        unequipItem({
          characterName,
          slotType,
        })
      ).unwrap()
    } catch (error) {
      console.error('Error desequipando item:', error)
      refreshInventory()
    }
  }

  const handleSwapEquipment = async (fromSlot, toSlot) => {
    try {
      dispatch(swapEquipmentLocal({ fromSlot, toSlot }))
      await dispatch(
        swapEquipment({
          characterName,
          fromSlot,
          toSlot,
        })
      ).unwrap()
    } catch (error) {
      console.error('Error intercambiando equipo:', error)
      refreshInventory()
    }
  }

  const handleMoveItem = async (fromIndex, toIndex) => {
    try {
      // Actualización optimista
      dispatch(moveItemLocal({ fromIndex, toIndex }))

      // Llamada a la API
      await dispatch(
        moveItem({
          characterName,
          fromSlot: fromIndex,
          toSlot: toIndex,
        })
      ).unwrap()
    } catch (error) {
      console.error('Error moviendo item:', error)
      refreshInventory()
    }
  }

  const handleClearError = () => {
    dispatch(clearError())
  }

  const refreshInventory = () => {
    if (characterName) {
      dispatch(fetchCharacterInventory(characterName))
    }
  }

  // Función helper para verificar si un item puede ser equipado en un slot
  const canEquipItemInSlot = (item, slotType) => canEquipItemInSlotUtil(item, slotType, equipment)

  // Función helper para obtener el total de stats con bonuses
  const getTotalStats = () => {
    const totalStats = {}

    Object.keys(character.stats).forEach((statName) => {
      const stat = character.stats[statName]
      totalStats[statName] = {
        base: stat.base,
        bonus: stat.bonus,
        total: stat.base + stat.bonus,
      }
    })

    return totalStats
  }

  // Función helper para calcular el daño total
  const getTotalDamage = () => {
    let totalMin = combat.damage.min
    let totalMax = combat.damage.max
    let weaponBonus = combat.weaponBonus

    // Añadir daño del arma principal
    if (equipment.mainHand && equipment.mainHand.damage) {
      const [min, max] = equipment.mainHand.damage.split('-').map(Number)
      totalMin += min || 0
      totalMax += max || 0
    }

    // Añadir bonus de stats
    const strengthBonus = Math.floor((character.stats.strength.base + character.stats.strength.bonus - 10) / 2)
    totalMin += Math.max(0, strengthBonus)
    totalMax += Math.max(0, strengthBonus)

    return {
      min: Math.max(1, totalMin),
      max: Math.max(1, totalMax),
      weaponBonus,
      critical: combat.critical,
    }
  }

  return {
    // Estado
    character,
    equipment,
    inventory,
    combat,
    loading,
    error,

    // Acciones
    handleEquipItem,
    handleUnequipItem,
    handleSwapEquipment,
    handleMoveItem,
    handleClearError,
    refreshInventory,

    // Helpers
    canEquipItemInSlot,
    getTotalStats,
    getTotalDamage,
  }
}
